import { format } from "date-fns";

import { ensureIsDate, getColor } from "@/utils";
import { i18n } from "@lingui/core";

import { setOptionFromDefault } from "./chart_options";

export const sumArray = (tot, val) => tot + val;

export const sortByDate = (a, b) => new Date(a[0]) - new Date(b[0]);
export const showOneDecimalPlace = (count, total) => {
  const percentWithOneDecimal = ((count / total) * 100).toFixed(1);
  // for certain widgets (share of voice, for example) count can sometimes be 0
  // for those cases, we want to show just "0%".
  if (count == 0) return 0;
  // however for very low percentages that would be rounded to less than 0.1%
  // we want to display "0.1%" instead of "0.0%" which would be the case if we
  // don't manually set it here
  if (percentWithOneDecimal == 0.0) return 0.1;
  else if (percentWithOneDecimal == 100.0) return 100;
  return percentWithOneDecimal;
};

const metricLabelMapping = {
  twitter: {
    like: L.REPORTS_WIDGET_ENGAGEMENT_TWITTER_LIKE_COUNT,
    retweet: L.REPORTS_WIDGET_ENGAGEMENT_TWITTER_RETWEET_COUNT,
  },
  facebook: {
    like: L.REPORTS_WIDGET_ENGAGEMENT_FACEBOOK_LIKE_COUNT,
    comment: L.REPORTS_WIDGET_ENGAGEMENT_FACEBOOK_COMMENT_COUNT,
    share: L.REPORTS_WIDGET_ENGAGEMENT_FACEBOOK_SHARE_COUNT,
    reaction: L.REPORTS_WIDGET_ENGAGEMENT_FACEBOOK_REACTION_COUNT,
  },
  youtube: {
    like: L.REPORTS_WIDGET_ENGAGEMENT_YOUTUBE_LIKE_COUNT,
    dislike: L.REPORTS_WIDGET_ENGAGEMENT_YOUTUBE_DISLIKE_COUNT,
    comment: L.REPORTS_WIDGET_ENGAGEMENT_YOUTUBE_COMMENT_COUNT,
    view: L.REPORTS_WIDGET_ENGAGEMENT_YOUTUBE_VIEW_COUNT,
  },
  tiktok: {
    like: L.REPORTS_WIDGET_ENGAGEMENT_TIKTOK_LIKE_COUNT,
    comment: L.REPORTS_WIDGET_ENGAGEMENT_TIKTOK_COMMENT_COUNT,
    view: L.REPORTS_WIDGET_ENGAGEMENT_TIKTOK_VIEW_COUNT,
    share: L.REPORTS_WIDGET_ENGAGEMENT_TIKTOK_SHARE_COUNT,
  },
  instagram: {
    like: L.REPORTS_WIDGET_ENGAGEMENT_INSTAGRAM_LIKE_COUNT,
    comment: L.REPORTS_WIDGET_ENGAGEMENT_INSTAGRAM_COMMENT_COUNT,
    view: L.REPORTS_WIDGET_ENGAGEMENT_INSTAGRAM_VIEW_COUNT,
  },
  linkedin: {
    reaction: L.REPORTS_WIDGET_ENGAGEMENT_LINKEDIN_REACTION_COUNT,
    comment: L.REPORTS_WIDGET_ENGAGEMENT_LINKEDIN_COMMENT_COUNT,
  },
};

const getTranslatableString = (s) => {
  const source = s.split("_")[0];
  const metric = s.split("_")[1];
  return metricLabelMapping[source][metric];
};

// Pie chart calculation helpers
export const getLabelsAndDatasetsForDoughnutChart = (key, data) => {
  let datasets = [],
    labels = [],
    backgroundColors = [];

  if (data) {
    const allSources = {};

    data.forEach((entry) => {
      entry[key].forEach(([source, count]) => {
        if (!allSources[source]) allSources[source] = 0;
        allSources[source] += count;
      });
    });

    const total = Object.values(allSources).reduce(sumArray, 0);

    // using intermediate arr to be able to sort later and keep track of labels
    let labeledValues = [];

    Object.entries(allSources).forEach(([source, count], i) => {
      labeledValues.push({
        label: i18n._(getTranslatableString(source)),
        value: showOneDecimalPlace(count, total),
      });

      backgroundColors.push(getColor(i, { title: source, scope: "mediaType" }));
    });

    labeledValues = labeledValues.sort((a, b) => b.value - a.value);

    labels = labeledValues.map((d) => d.label);
    datasets = labeledValues.map((d) => d.value);

    datasets = [
      {
        data: datasets,
        borderWidth: 0,
        backgroundColor: backgroundColors,
      },
    ];
  }

  return { labels, datasets };
};

export const getLabelsAndDatasetsForPieChart = (
  key,
  data,
  convertToPercentage
) => {
  let datasets = [],
    labels = [],
    backgroundColors = [],
    sources = [],
    sortedLabeledValues;

  if (data) {
    let sum = 0;

    data.forEach((entry, idx) => {
      const totals = (entry[key] || []).map((i) => i[1]),
        value = parseInt(totals.reduce(sumArray, 0)),
        title = entry.meta.title;

      sum += value;
      datasets.push(value);
      labels.push(title);

      backgroundColors.push(getColor(idx, { title: title, scope: "source" }));

      // store source type and id for MentionModal
      // value is used for sorting
      sources.push({ id: entry.meta.id, type: entry.meta.type, value });
    });

    if (convertToPercentage) {
      // override datasets with % version
      datasets = datasets.map(
        (value, idx) => (datasets[idx] = showOneDecimalPlace(value, sum))
      );
    }

    const sortByValue = (a, b) => b.value - a.value;

    sortedLabeledValues = datasets
      .map((value, idx) => ({
        value,
        label: labels[idx],
      }))
      .sort(sortByValue);

    // use the same sorting for `sources` so we can reference sources using the
    // same indexed as we do for datasets and labels.
    // `sources` are used to get which alert to show when showing the MentionsModal
    sources.sort(sortByValue);

    datasets = sortedLabeledValues?.map((d) => d.value);
    labels = sortedLabeledValues?.map((d) => d.label);

    datasets = [
      {
        data: datasets,
        borderWidth: 0,
        backgroundColor: backgroundColors,
        hoverBackgroundColor: backgroundColors,
        sources: sources,
      },
    ];
  }

  return { labels, datasets };
};

// Line/area chart calculation helpers
export const getLabelsAndDatasetsForLineChart = (key, data, isStacked) => {
  const getLabels = () => (data[0][key] || []).map((i) => i[0]);

  const getDatasets = () => {
    return data.map((entry, idx) => {
      const totals = (entry[key] || []).map((i) => parseInt(i[1]));
      const sourceTitle = entry.meta.title;

      return {
        data: totals,
        label: sourceTitle,
        ...setOptionFromDefault(idx, isStacked, {
          title: sourceTitle,
          scope: "source",
        }),

        // store source type and id for MentionModal
        source: { id: entry.meta.id, type: entry.meta.type },
      };
    });
  };

  const labels = data ? getLabels() : [];
  const datasets = data ? getDatasets() : [];

  return { labels, datasets };
};

// group key by media type
export const getLabelsAndDatasetsGroupedByMediaType = (
  key,
  data,
  isStacked = true
) => {
  const getLabels = () => {
    return (data[0][key] || []).map((reach) => reach[0]);
  };

  const getDatasets = () => {
    const allSources = {};
    const datasets = [];
    const defaultSources = {
      Podcast: 0,
      Radio: 0,
      YouTube: 0,
      TV: 0,
      "Web TV": 0,
      "Manual uploads": 0,
    };

    // set dates with counts for every media type and sum
    data.forEach((entry) => {
      const amounts = entry[key] || [];
      amounts.forEach(([date, sourcesAndCounts]) => {
        const allSourcesAndCounts = { ...defaultSources, ...sourcesAndCounts };

        Object.entries(allSourcesAndCounts).forEach(([source, count]) => {
          if (!allSources[source]) allSources[source] = {};
          if (!allSources[source][date]) allSources[source][date] = 0;
          allSources[source][date] += parseInt(count);
        });
      });
    });

    // calculate totals for every media type
    Object.entries(allSources).forEach(([source, countsWithDates]) => {
      const counts = Object.values(countsWithDates);

      if (counts.reduce(sumArray, 0) !== 0) {
        const i = datasets.length;

        datasets.push({
          label: source,
          data: counts,
          ...setOptionFromDefault(i, isStacked, {
            title: source,
            scope: "mediaType",
          }),
        });
      }
    });
    return datasets;
  };

  const labels = data ? getLabels() : [];
  const datasets = data ? getDatasets() : [];

  return { labels, datasets };
};

export const poll = async ({
  fn,
  validate,
  interval,
  maxAttempts,
  checkError,
}) => {
  let attempts = 0;

  const executePoll = async (resolve, reject) => {
    const result = await fn();
    attempts++;

    if (validate(result)) {
      return resolve(result);
    } else if (checkError(result)) {
      return reject(new Error("Task failure"));
    } else if (maxAttempts && attempts === maxAttempts) {
      return reject(new Error("Exceeded max attempts"));
    } else {
      setTimeout(executePoll, interval, resolve, reject);
    }
  };

  return new Promise(executePoll);
};

export const widgetTitle = ({
  title,
  metric,
  metricDisplay,
  chartType,
  searches,
  tags,
  reportTemplate,
  interval,
  startDate,
}) => {
  // just use the given title if one is set
  if (title) return title;

  let type = "";

  // top_channels, topics and mentions don't have any chart types
  if (
    metric !== "top_channels" &&
    metric !== "topics" &&
    metric !== "mentions" &&
    metric !== "word_cloud"
  ) {
    type = "_" + chartType;
  }

  // get default title based on metric and chart type from L object
  let newTitle =
    L[`REPORTS_WIDGET_DEFAULT_TITLE_${metric}${type}`.toUpperCase()];

  // fallback to formatted metric
  if (!newTitle) newTitle = metricDisplay;

  // include source title if it's a comparison report
  if (newTitle && (reportTemplate || "").indexOf("comparison") === 0) {
    if (searches.length === 1 && tags.length === 0) {
      newTitle += ` – ${searches[0].title}`;
    } else if (tags.length === 1 && searches.length === 0) {
      newTitle += ` – ${tags[0].name}`;
    }
  }

  if (reportTemplate === "time_comparison") {
    const displayFormat = {
      yearly: "yyyy",
      quarterly: "QQQ, yyyy",
      monthly: "MMM, yyyy",
      weekly: "yyyy-MM-dd",
      daily: "yyyy-MM-dd",
    }[interval];

    newTitle += ` – ${format(ensureIsDate(startDate), displayFormat)}`;
  }

  return newTitle;
};
